
import React from 'react';
import { useCostEstimator } from '@/context/CostEstimatorContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { CheckIcon, HeartPulseIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const MaintenanceStep: React.FC = () => {
  const { setMaintenance, options, goToNextStep, goToPreviousStep, COSTS } = useCostEstimator();

  const maintenanceOptions = [
    { value: '6', label: '6 Month Maintenance', description: 'Bug fixes and critical updates for 6 months', cost: COSTS.maintenance['6'] },
    { value: '12', label: '12 Month Maintenance', description: 'Bug fixes, updates, and minor enhancements for 1 year', cost: COSTS.maintenance['12'] },
    { value: '24', label: '24 Month Maintenance', description: 'Full support, updates, and feature enhancements for 2 years', cost: COSTS.maintenance['24'] },
  ];

  return (
    <Card className="form-card">
      <CardHeader>
        <CardTitle>Do you need maintenance & support?</CardTitle>
        <CardDescription>Select a maintenance plan or skip this step</CardDescription>
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={options.maintenance}
          onValueChange={(value) => setMaintenance(value as any)}
          className="grid gap-4"
        >
          {maintenanceOptions.map((option) => (
            <div
              key={option.value}
              className={cn(
                "border rounded-lg p-4 cursor-pointer transition-all duration-300",
                options.maintenance === option.value 
                  ? "border-primary bg-primary/10" 
                  : "border-border hover:border-primary/50"
              )}
              onClick={() => setMaintenance(option.value as any)}
            >
              <div className="flex items-start space-x-4">
                <RadioGroupItem value={option.value} id={`maintenance-${option.value}`} className="mt-1" />
                <div className="flex-1">
                  <div className="flex justify-between">
                    <Label htmlFor={`maintenance-${option.value}`} className="font-medium">
                      {option.label}
                    </Label>
                    <span className="text-primary font-medium">${option.cost.toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
                </div>
              </div>
            </div>
          ))}
          
          <div
            className={cn(
              "border rounded-lg p-4 cursor-pointer transition-all duration-300",
              !options.maintenance 
                ? "border-primary bg-primary/10" 
                : "border-border hover:border-primary/50"
            )}
            onClick={() => setMaintenance(undefined)}
          >
            <div className="flex items-center">
              <RadioGroupItem value="" id="maintenance-none" />
              <Label htmlFor="maintenance-none" className="ml-2 font-medium">
                No maintenance plan needed
              </Label>
            </div>
          </div>
        </RadioGroup>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={goToPreviousStep}>
          Previous
        </Button>
        <Button onClick={goToNextStep}>
          See Results
        </Button>
      </CardFooter>
    </Card>
  );
};

export default MaintenanceStep;
